// iot-admin/client/src/v2/domains/demand/Review.js
//
// Demand → Review queue. THE place a moderation decision is made.
//
// Founder, 2026-09-14: approvals in Brands Manager were a pilot hack. They are
// removed there; this screen is the only surface where a decision happens, and
// it is super-admin only (the server mounts /api/moderation behind
// requireSuperAdminAuth).
//
// ── WHAT THIS SCREEN IS FOR ─────────────────────────────────────────────────
// Not "show me the queue". The operator's question is "what needs me, and can I
// decide it from what is on this screen". So the screen has to do three things
// the previous cut did not:
//
//   SHOW THE THING. Half the live queue is mp4. The classic screen rendered a
//   40px muted <video> with no controls — a poster frame. You cannot moderate an
//   ad you have not watched, and you cannot judge audio you were never played.
//   The drawer stages the creative at size, with controls, unmuted.
//
//   SHOW THE DIFFERENCE. A change request is a list of field edits. Before and
//   after sit on one row so the change is the thing you read, not something you
//   reconstruct. A creative swap gets the live one and the proposed one side by
//   side — that comparison IS the decision.
//
//   SAY WHAT IS BEING ASKED. Two different questions can be open on one ad at
//   the same time, and they are not the same question.
//
// ── THE TWO-DECISIONS-ONE-AD CASE, AND WHY THERE ARE STILL TWO ROWS ─────────
// An ad can be waiting on "may this run at all" (brand_ads.review_status) and on
// "may this new creative replace the live one" (a change request carrying a
// creativePinsPatch) simultaneously. Approving one does not resolve the other:
// the change-request path writes ads_creatives_map, the ad-review path writes
// brand_ads. Two decisions.
//
// So the queue keeps TWO ROWS — collapsing them would make "waiting on you: 6"
// read as 5 when six decisions are outstanding, and a row that promises "you can
// act on this" must be exactly one act. But neither may be taken in ignorance of
// the other, so the open pane ALWAYS carries the tied counterpart with its own
// buttons, and the row carries a chip saying the tie exists.
//
// Order matters and the screen says so: decide the swap first. The ad-review
// row's creative comes from a LATERAL ordered by `is_live DESC`
// (server/routes/moderation.js), which means it shows the creative that is LIVE
// — the OLD one — whenever a swap is pending. Judge the ad on the creative that
// will actually run.
//
// ── THIS CONSOLE IS NEVER A SECOND WRITER ───────────────────────────────────
// Every decision is a thin proxy to galigali-in's V5 engine, carrying the
// admin's own Bearer token (server/routes/moderation.js proxyDecision). There is
// no direct-DB fallback here on purpose: the approve path drags the whole V5
// rules engine, and a second writer would drift from the delivery gate.
//
// ── THE FEEDBACK KEY IS `message` ───────────────────────────────────────────
// Load-bearing. The advertiser's renderer reads reason|message|feedback|note and
// the notification builder reads reasons[0]|message|note. `message` is the only
// key BOTH understand. The Brands Manager inbox wrote `{reasons:[…]}`, which the
// advertiser's screen rendered as blank — told no, not told why.

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Info, CheckCircle2, X, RefreshCw, Film, Image as ImageIcon } from 'lucide-react';
import { PageHead, Tiles, Pill, Loading, ErrorState, Empty, Button } from '../../components/ui';
import { Drawer, RvCard } from '../fleet/Onboarding';
import s from '../fleet/Onboarding.module.css';
import c from './Review.module.css';
import useApi from '../../lib/useApi';
import api from '../../../services/api';

const GRID = {
  gridTemplateColumns: '52px minmax(220px, 1.4fr) minmax(150px, .9fr) 110px minmax(120px, .7fr)',
};

const fmtWhen = (t) => (t
  ? new Date(t).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  })
  : '—');

const waitDays = (t) => (t ? Math.floor((Date.now() - new Date(t).getTime()) / 86400000) : null);

const waitLabel = (t) => {
  const d = waitDays(t);
  if (d === null) return '—';
  if (d === 0) return 'today';
  if (d === 1) return '1 day';
  return `${d} days`;
};

// A wait long enough to be worth flagging. Measured decision latency once a
// super-admin is looking is about five seconds — anything sitting for days was
// never seen, not deliberated over.
const STALE_DAYS = 3;

// The queue holds one state — waiting — so a status pill in here would be a
// column that always says the same word. The pill labels the KIND instead, and
// is toned by kind so the two decisions are distinguishable at a glance rather
// than being two identically-coloured chips with different text.
const KIND_TONE = { ad: 'pend', cr: 'iris' };

// The server answers {success, data, count}; axios wraps that again. One place
// to unwrap, and it THROWS on a shape it does not recognise rather than
// rendering an empty queue that looks like "nothing to do" — the most dangerous
// thing this screen could get wrong.
function rows(res) {
  const body = res && res.data;
  if (!body || body.success !== true || !Array.isArray(body.data)) {
    throw new Error('The review service answered in a shape this screen does not understand.');
  }
  return body.data;
}

// ── value rendering for the diff ────────────────────────────────────────────
// Absent, explicitly-cleared and empty are three different facts and a reviewer
// has to be able to tell them apart before approving. JSON.stringify drops
// undefined, so an absent `old` is normal, not a bug.
function Value({ v, money }) {
  if (v === undefined) return <span className={c.diffUnset}>not set</span>;
  if (v === null) return <span className={c.diffUnset}>cleared</span>;
  if (v === '') return <span className={c.diffUnset}>empty</span>;
  if (typeof v === 'boolean') return <>{v ? 'Yes' : 'No'}</>;
  if (typeof v === 'number' && money) return <>{`₹${v.toLocaleString('en-IN')}`}</>;
  if (typeof v === 'object') return <>{JSON.stringify(v)}</>;
  return <>{String(v)}</>;
}

// An explicit allowlist, never a /budget/ regex — `adset_budget_sharing_max_pct`
// matches that regex and is a percentage, so a regex would print ₹80 for 80%.
const MONEY_FIELDS = new Set([
  'budget_amount_daily', 'budget_amount_lifetime', 'advantage_campaign_budget_amount',
  'bid_amount', 'spend_cap',
]);

const SCOPE_LABEL = { campaign: 'Campaign', adset: 'Ad set', ad: 'Ad' };

function Diff({ summary, creativeShownAbove }) {
  // When a creative swap is rendered as a side-by-side comparison above, the
  // diff row for that same creative is worse than redundant: its `old` is
  // ABSENT (the request records what is being pinned, not what was there), so
  // it prints "not set" directly under a panel showing the creative that is
  // running today. Two true statements that read as a contradiction. The
  // comparison IS the diff for that field, so the row goes.
  const all = Array.isArray(summary) ? summary : [];
  const list = creativeShownAbove ? all.filter((r) => r.field !== 'creative') : all;
  if (!list.length && !creativeShownAbove) {
    // A real row in production carries summary = []. Saying "no changes" would
    // be a guess; saying what we actually know is the honest answer, and it
    // tells the reviewer the right next move.
    return (
      <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-mute)' }}>
        This request arrived without a readable list of changes. Nothing here says what
        it would alter, so there is nothing to approve on the strength of — reject it and
        ask for a fresh submission.
      </p>
    );
  }
  if (!list.length) {
    return (
      <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-mute)' }}>
        The new creative above is the whole of this change — nothing else is altered.
      </p>
    );
  }
  return (
    <table className={c.diff}>
      <thead>
        <tr>
          <th style={{ width: '38%' }}>What changes</th>
          <th>Now</th>
          <th style={{ width: 28 }} aria-hidden="true" />
          <th>Proposed</th>
        </tr>
      </thead>
      <tbody>
        {list.map((r, i) => {
          const money = MONEY_FIELDS.has(r.field);
          return (
            <tr key={`${r.entityType}-${r.entityId}-${r.field}-${i}`}>
              <td>
                <span className={c.diffLabel}>{r.label || r.field || 'Unnamed field'}</span>
                {r.derived && <span className={c.diffDerived}>(follows from another edit)</span>}
                <span className={c.diffScope}>
                  {SCOPE_LABEL[r.entityType] || r.entityType || 'Item'}
                </span>
              </td>
              <td className={c.diffOld}><Value v={r.old} money={money} /></td>
              <td className={c.diffArrow} aria-hidden="true">→</td>
              <td className={c.diffNew}><Value v={r.new} money={money} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── the creative ────────────────────────────────────────────────────────────
// The presigned URL is good for 300 seconds. A drawer left open longer shows a
// broken image with no explanation, so the stage tracks its own age and offers
// the reload rather than looking defective.
const PRESIGN_TTL_MS = 300 * 1000;

function Stage({ creativeId, mime, label }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [mintedAt, setMintedAt] = useState(0);
  const [stale, setStale] = useState(false);

  const mint = useCallback(async () => {
    if (!creativeId) return;
    setErr(null);
    try {
      const r = await api.get(`/moderation/creatives/${creativeId}/preview-url`);
      const d = r && r.data && r.data.data;
      if (!d || !d.url) throw new Error('No preview available for this creative.');
      setUrl(d.url);
      setMintedAt(Date.now());
      setStale(false);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    }
  }, [creativeId]);

  useEffect(() => { mint(); }, [mint]);

  useEffect(() => {
    if (!mintedAt) return undefined;
    const t = setTimeout(() => setStale(true), PRESIGN_TTL_MS - 15000);
    return () => clearTimeout(t);
  }, [mintedAt]);

  const isVideo = String(mime || '').startsWith('video/');

  if (!creativeId) {
    return (
      <div className={c.stage}>
        <span className={c.stageEmpty}>No creative is attached to this ad yet.</span>
      </div>
    );
  }
  if (err) {
    return (
      <>
        <div className={c.stage}><span className={c.stageEmpty}>{err}</span></div>
        <div className={c.stageNote}>
          <Button onClick={mint}><RefreshCw size={13} aria-hidden="true" /> Try again</Button>
        </div>
      </>
    );
  }
  return (
    <>
      <div className={c.stage}>
        {!url && <span className={c.stageEmpty}>Loading…</span>}
        {url && (isVideo
          // Controls, and NOT muted: the audio track is part of what is being
          // moderated. No autoplay, so nothing blasts sound unasked.
          ? <video src={url} controls preload="metadata" playsInline onError={() => setStale(true)} />
          : <img src={url} alt={label || ''} onError={() => setStale(true)} />)}
      </div>
      <div className={c.stageNote}>
        {isVideo ? <Film size={13} aria-hidden="true" /> : <ImageIcon size={13} aria-hidden="true" />}
        <span>{label}</span>
        <span style={{ color: 'var(--ink-faint)' }}>{mime}</span>
        {stale && (
          <>
            <span className={c.stageStale}>This preview link has expired.</span>
            <Button onClick={mint}><RefreshCw size={13} aria-hidden="true" /> Reload</Button>
          </>
        )}
      </div>
    </>
  );
}

// A 40px recognition aid in the list. The full judgement happens in the drawer.
function Thumb({ creativeId, mime }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let dead = false;
    if (!creativeId) return undefined;
    api.get(`/moderation/creatives/${creativeId}/preview-url`)
      .then((r) => { if (!dead) setUrl(r?.data?.data?.url || null); })
      .catch(() => { if (!dead) setFailed(true); });
    return () => { dead = true; };
  }, [creativeId]);
  const isVideo = String(mime || '').startsWith('video/');
  if (!creativeId || failed || !url) {
    return (
      <span className={`${c.thumb} ${c.thumbEmpty}`} aria-hidden="true">
        {isVideo ? <Film size={15} /> : <ImageIcon size={15} />}
      </span>
    );
  }
  return isVideo
    ? <video className={c.thumb} src={url} muted playsInline preload="metadata" />
    : <img className={c.thumb} src={url} alt="" />;
}

// ── the decision control ────────────────────────────────────────────────────
// Replaces window.prompt(). Approve is one click; reject arms a box first,
// because the sentence typed there is shown to the advertiser and a prompt
// dialog is not a place anyone writes a sentence they mean.
function Decide({ kind, busy, onApprove, onReject }) {
  const [arming, setArming] = useState(false);
  const [reason, setReason] = useState('');
  const what = kind === 'ad' ? 'this ad' : 'this change';

  if (!arming) {
    return (
      <div className={c.armActions}>
        <Button primary disabled={busy} onClick={onApprove}>
          <CheckCircle2 size={14} aria-hidden="true" /> {busy ? 'Working…' : `Approve ${what}`}
        </Button>
        <Button disabled={busy} onClick={() => setArming(true)}>
          <X size={14} aria-hidden="true" /> Reject
        </Button>
      </div>
    );
  }
  return (
    <div className={c.arm}>
      <div className={c.armTitle}>Why are you rejecting {what}?</div>
      <div className={c.armHint}>
        The advertiser sees this on their own screen, word for word. Tell them what to
        change so the next submission can pass.
      </div>
      <textarea
        className={c.reason}
        value={reason}
        autoFocus
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. The logo is cropped on the right edge — please re-export at 1080×1920 with the full lockup visible."
      />
      <div className={c.armActions}>
        <Button
          primary
          disabled={busy || reason.trim().length < 4}
          onClick={() => onReject(reason.trim())}
        >
          {busy ? 'Working…' : 'Reject and send this reason'}
        </Button>
        <Button disabled={busy} onClick={() => { setArming(false); setReason(''); }}>Cancel</Button>
      </div>
    </div>
  );
}

export default function Review() {
  const [params, setParams] = useSearchParams();
  const openKey = params.get('open') || '';
  const kindFilter = params.get('kind') || '';
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState(null);
  const [failed, setFailed] = useState(null);

  const data = useApi(() => Promise.all([
    // 200, not 500: the server clamps at 200, and asking for 500 was a silent
    // lie about how much of the queue you were seeing.
    api.get('/moderation/ad-reviews', { params: { status: 'pending_review', limit: 200 } }),
    api.get('/moderation/change-requests', { params: { status: 'open', limit: 200 } }),
  ]).then(([a, b]) => ({ ads: rows(a), crs: rows(b) })), []);

  const { ads = [], crs = [] } = data.data || {};

  // The tie: a change request whose creativePinsPatch names an ad that is ALSO
  // sitting in the ad-review queue. Resolved here rather than server-side
  // because it is a fact about two queues, and only this screen holds both.
  const pinnedAdIds = new Map();
  for (const cr of crs) {
    for (const p of cr.creative_pins || []) {
      if (p.brand_ad_id) pinnedAdIds.set(p.brand_ad_id, cr);
    }
  }

  const adRows = ads.map((r) => ({
    key: `ad:${r.id}`, kind: 'ad', id: r.id, raw: r,
    what: r.ad_name, sub: `${r.campaign_name || '—'} · ${r.adset_name || '—'}`,
    tenant: r.tenant_name, state: r.review_status, since: r.updated_at || r.created_at,
    creativeId: r.creative_id, creativeMime: r.creative_mime, creativeName: r.creative_name,
    tie: pinnedAdIds.get(r.id) || null,
  }));
  const crRows = crs.map((r) => ({
    key: `cr:${r.id}`, kind: 'cr', id: r.id, raw: r,
    what: r.campaign_name || 'Campaign change',
    sub: `${(Array.isArray(r.summary) ? r.summary.length : 0)} change${(Array.isArray(r.summary) ? r.summary.length : 0) === 1 ? '' : 's'}`
      + (r.creative_pins ? ' · new creative' : ''),
    tenant: r.tenant_name, state: r.status, since: r.submitted_at || r.created_at,
    creativeId: (r.creative_pins && r.creative_pins[0] && r.creative_pins[0].brand_creative_id) || null,
    creativeMime: (r.creative_pins && r.creative_pins[0] && r.creative_pins[0].creative_mime) || null,
    creativeName: (r.creative_pins && r.creative_pins[0] && r.creative_pins[0].creative_name) || null,
    tie: (r.creative_pins || []).some((p) => ads.find((a) => a.id === p.brand_ad_id))
      ? ads.find((a) => (r.creative_pins || []).some((p) => p.brand_ad_id === a.id)) : null,
  }));

  const all = [...adRows, ...crRows]
    .filter((r) => (!kindFilter || r.kind === kindFilter))
    .filter((r) => {
      if (!q.trim()) return true;
      const hay = `${r.what} ${r.sub} ${r.tenant}`.toLowerCase();
      return hay.includes(q.trim().toLowerCase());
    })
    .sort((a, b) => new Date(a.since || 0) - new Date(b.since || 0));

  const open = all.find((r) => r.key === openKey)
    || [...adRows, ...crRows].find((r) => r.key === openKey) || null;

  const close = () => setParams((p) => { p.delete('open'); return p; }, { replace: false });

  async function act(row, fn, sentence) {
    setBusy(row.key); setFailed(null); setDone(null);
    try {
      await fn();
      setDone(sentence);
      close();
      await data.reload();
    } catch (e) {
      // A decision that failed must say so loudly — the row is still there and
      // a reviewer who assumes it went through will not come back to it.
      setFailed(e?.response?.data?.error || e.message || 'The decision could not be recorded.');
    } finally { setBusy(null); }
  }

  const approveAd = (row) => act(row,
    () => api.post(`/moderation/ads/${row.id}/review`, { decision: 'approved', feedback: null }),
    `Approved “${row.what}”. It can run as soon as its campaign is live.`);
  const rejectAd = (row, message) => act(row,
    () => api.post(`/moderation/ads/${row.id}/review`, { decision: 'disapproved', feedback: { message } }),
    `Rejected “${row.what}”. The advertiser has been given your reason.`);
  const approveCr = (row) => act(row,
    () => api.post(`/moderation/change-requests/${row.id}/approve`, {}),
    `Approved the change to “${row.what}”. It is being applied to the live campaign.`);
  const rejectCr = (row, message) => act(row,
    () => api.post(`/moderation/change-requests/${row.id}/reject`, { feedback: { message } }),
    `Rejected the change to “${row.what}”. The advertiser has been given your reason.`);

  const head = (
    <PageHead
      title="Review queue"
      sub="Every decision waiting on a super-admin, across every workspace. Two questions live here: may an ad run at all, and may an edit to a live campaign be applied. Both are recorded by the Brands Manager engine — this console asks, it never writes."
      actions={<Button onClick={data.reload}><RefreshCw size={14} aria-hidden="true" /> Refresh</Button>}
    />
  );

  if (data.loading && !data.data) return <>{head}<Loading what="the review queue" /></>;
  if (data.error) return <>{head}<ErrorState error={data.error} onRetry={data.reload} /></>;

  const oldest = all.length ? waitDays(all[0].since) : null;

  return (
    <>
      {head}

      {done && <div className={c.done}>{done}</div>}
      {failed && <div className={c.failed}>{failed}</div>}

      <Tiles items={[
        { label: 'Waiting on you', value: adRows.length + crRows.length,
          tone: (adRows.length + crRows.length) ? 'warn' : 'grow',
          foot: 'every open decision' },
        { label: 'Ads to review', value: adRows.length, foot: 'may this run at all' },
        { label: 'Campaign changes', value: crRows.length, foot: 'may this edit be applied' },
        { label: 'Longest wait', value: oldest === null ? '—' : `${oldest}d`,
          tone: oldest !== null && oldest >= STALE_DAYS ? 'bad' : undefined,
          foot: oldest !== null && oldest >= STALE_DAYS ? 'nobody has looked' : 'within a day' },
      ]}
      />

      <div className={s.searchBar}>
        <Search size={15} aria-hidden="true" style={{ color: 'var(--ink-mute)', flex: 'none' }} />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search — ad, campaign, workspace…" aria-label="Search the review queue" />
      </div>

      <div className={s.sheet}>
        <div className={s.toolbar}>
          {[['', 'all'], ['ad', 'ads'], ['cr', 'changes']].map(([k, label]) => {
            const n = k === '' ? adRows.length + crRows.length : (k === 'ad' ? adRows.length : crRows.length);
            return (
              <button key={k || 'all'} type="button"
                      className={`${s.chip} ${kindFilter === k ? s.chipOn : ''}`}
                      onClick={() => setParams((p) => {
                        if (k) p.set('kind', k); else p.delete('kind'); return p;
                      }, { replace: true })}>
                {label} · {n}
              </button>
            );
          })}
        </div>

        <div className={s.noteBar}>
          Oldest first — the thing that has waited longest is the thing most likely never
          to have been seen. An ad and a change can both be open on the same ad; they are
          different decisions and neither settles the other, so decide the new creative
          first and judge the ad on what will actually run.
        </div>

        <div className={`${s.regRow} ${s.thRow}`} style={GRID}>
          <span className={s.th} aria-label="Creative" />
          <span className={s.th}>What needs deciding</span>
          <span className={s.th}>Workspace</span>
          <span className={s.th}>Kind
            <span className={s.helpDot} title="Ad — may this ad run at all. Change — may this edit to a live campaign be applied.">
              <Info size={11} />
            </span>
          </span>
          <span className={s.th}>Waiting</span>
        </div>

        {!all.length && (
          <Empty title="Nothing is waiting on you">
            {q || kindFilter
              ? 'No open decision matches that filter.'
              : 'Every ad and every campaign change has been decided. New submissions land here, and the super-admins are emailed.'}
          </Empty>
        )}

        {all.map((r) => (
          <div key={r.key} className={s.regRow} style={GRID} role="button" tabIndex={0}
               onClick={() => setParams((p) => { p.set('open', r.key); return p; }, { replace: false })}
               onKeyDown={(e) => { if (e.key === 'Enter') setParams((p) => { p.set('open', r.key); return p; }); }}>
            <Thumb creativeId={r.creativeId} mime={r.creativeMime} />
            <span className={s.regPrimary} style={{ fontFamily: 'inherit' }}>
              {r.what}
              {r.tie && <span className={c.tie}>+1 tied decision</span>}
              <small>{r.sub}</small>
            </span>
            <span>{r.tenant}</span>
            <span>
              <Pill tone={KIND_TONE[r.kind]}>{r.kind === 'ad' ? 'Ad' : 'Change'}</Pill>
            </span>
            <span style={{ color: waitDays(r.since) >= STALE_DAYS ? 'var(--bad-text)' : 'var(--ink-soft)' }}>
              {waitLabel(r.since)}
            </span>
          </div>
        ))}

        {all.length > 0 && (
          <div className={s.resultsFoot}>
            {all.length} open decision{all.length === 1 ? '' : 's'}
            {(ads.length === 200 || crs.length === 200)
              && ' · showing the first 200 of a queue the server caps here — filter to narrow it'}
          </div>
        )}
      </div>

      {open && (
        <Drawer
          eyebrow={open.kind === 'ad' ? 'Ad review' : 'Campaign change'}
          title={open.what}
          sub={`${open.tenant} · submitted ${fmtWhen(open.since)} · waiting ${waitLabel(open.since)}`}
          footer={<span className={c.idFoot}>{open.kind === 'ad' ? 'Ad' : 'Request'} {open.id}</span>}
          onClose={close}
        >
          {/* secBody, NOT drawerBody. drawerBody is a `138px 1fr` grid built for
              EntityDrawer's section rail — dropping plain children into it put
              the creative (the thing you judge) in the 138px column and a wall
              of label/value rows in the wide one. secBody is the single
              scrolling column a reading pane actually wants. */}
          <div className={s.secBody}>
            {failed && <div className={c.failed}>{failed}</div>}

            {open.kind === 'ad' ? (
              <>
                <p className={c.ctx}>
                  in <span className={c.ctxPath}>{open.raw.campaign_name || '—'}</span>
                  {' › '}<span className={c.ctxPath}>{open.raw.adset_name || '—'}</span>
                </p>

                <RvCard title="The creative">
                  {open.tie && (
                    <p style={{ margin: '0 0 var(--s3)', fontSize: 12.5, color: 'var(--warn-text)' }}>
                      A new creative is waiting to replace this one. What you see below is what
                      is LIVE today, not what would run if that change is approved — decide the
                      change first.
                    </p>
                  )}
                  <Stage creativeId={open.creativeId} mime={open.creativeMime} label={open.creativeName} />
                </RvCard>

                {!open.raw.is_published && (
                  <div className={c.alert}>
                    This campaign is not published. Approving lets the ad run, but nothing
                    will deliver until the advertiser publishes.
                  </div>
                )}

                <Decide
                  kind="ad" busy={busy === open.key}
                  onApprove={() => approveAd(open)}
                  onReject={(m) => rejectAd(open, m)}
                />

                {open.tie && (
                  <div className={c.tieCard}>
                    <div className={c.tieCardHead}>Also waiting on this ad — a new creative</div>
                    <p style={{ margin: '0 0 var(--s2)', fontSize: 12.5, color: 'var(--ink-soft)' }}>
                      “{open.tie.campaign_name}” has a change request that would replace this ad’s
                      creative. Approving the ad does not approve that swap, and approving the swap
                      does not approve the ad.
                    </p>
                    <Button onClick={() => setParams((p) => { p.set('open', `cr:${open.tie.id}`); return p; })}>
                      Open the creative change
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className={c.ctx}>
                  asked for by <span className={c.ctxPath}>{open.raw.submitted_by_name || 'someone in this workspace'}</span>
                </p>

                {open.raw.creative_pins && open.raw.creative_pins.length > 0 && (
                  <RvCard title="The creative being swapped in">
                    <div className={c.compare}>
                      <div>
                        <div className={c.compareHead}>Running today</div>
                        {open.tie
                          ? <Stage creativeId={open.tie.creative_id} mime={open.tie.creative_mime}
                                   label={open.tie.creative_name} />
                          : <div className={c.stage}>
                              <span className={c.stageEmpty}>
                                Nothing is running on this ad yet.
                              </span>
                            </div>}
                      </div>
                      <div>
                        <div className={`${c.compareHead} ${c.compareHeadNew}`}>Proposed</div>
                        <Stage creativeId={open.creativeId} mime={open.creativeMime}
                               label={open.creativeName} />
                      </div>
                    </div>
                  </RvCard>
                )}

                <RvCard title="What changes">
                  <Diff
                    summary={open.raw.summary}
                    creativeShownAbove={!!(open.raw.creative_pins && open.raw.creative_pins.length)}
                  />
                </RvCard>

                {/* A request mid-apply is not a decision anybody can take — the
                    engine is already writing. Saying so beats a button that
                    would be refused. */}
                {open.state === 'applying' ? (
                  <p style={{ fontSize: 13, color: 'var(--ink-mute)' }}>
                    This change is being applied right now. Refresh in a moment to see where it lands.
                  </p>
                ) : (
                  <Decide
                    kind="cr" busy={busy === open.key}
                    onApprove={() => approveCr(open)}
                    onReject={(m) => rejectCr(open, m)}
                  />
                )}
              </>
            )}
          </div>
        </Drawer>
      )}
    </>
  );
}
